// Wear compliance — how much of the data behind every other number is real.
//
// Sits alongside the FM-15 data-quality filter: the filter decides which cohorts
// count, this table shows why a cohort would be dropped.

import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import type { WearComplianceSummary } from '../data/types';
import { RELIABLE_CONFIDENCE_MIN } from '../data/methodology';

const Meter: React.FC<{ value: number; tone: 'neutral' | 'warn' }> = ({ value, tone }) => (
  <span className='flex items-center gap-2'>
    <span className='h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-muted'>
      <span
        className={cn('block h-full rounded-full', tone === 'warn' ? 'bg-warning' : 'bg-primary')}
        style={{ width: `${Math.min(Math.max(value, 0), 100)}%` }}
      />
    </span>
    <span className='tabular-nums'>{value}%</span>
  </span>
);

export const WearCompliancePanel: React.FC<{ rows: WearComplianceSummary[] }> = ({ rows }) => (
  <div className='overflow-x-auto'>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Team</TableHead>
          <TableHead className='text-right'>Wearing</TableHead>
          <TableHead>Coverage</TableHead>
          <TableHead>Confidence</TableHead>
          <TableHead className='text-right'>Missing days</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const belowThreshold = row.confidenceScore < RELIABLE_CONFIDENCE_MIN;
          return (
            <TableRow key={row.cohortId}>
              <TableCell className='font-medium'>
                {row.cohortName}
                {belowThreshold && (
                  <span className='block text-xs font-normal text-warning'>
                    Below the {RELIABLE_CONFIDENCE_MIN}% reliable-data threshold
                  </span>
                )}
              </TableCell>
              <TableCell className='text-right tabular-nums'>
                {row.activeUsers}/{row.enrolledUsers}
              </TableCell>
              <TableCell>
                <Meter value={row.coveragePct} tone='neutral' />
              </TableCell>
              <TableCell>
                <Meter value={row.confidenceScore} tone={belowThreshold ? 'warn' : 'neutral'} />
              </TableCell>
              <TableCell className='text-right tabular-nums text-muted-foreground'>{row.missingDataDays}</TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </div>
);
